import { Crown, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface AssigneeRowActionsProps {
    userPid: string;
    name: string;
    isDri: boolean;
    /** When true, Set DRI / Remove lock: task-level, owned by the DRI/creator/admin. */
    rosterLocked: boolean;
    onSetDri: (pid: string) => void;
    onRemove: (pid: string) => void;
}

/** Set-DRI and remove-assignee buttons for one assignee row — split out of AssigneeRow to keep that file under the repo's line limit. */
export function AssigneeRowActions({
    userPid,
    name,
    isDri,
    rosterLocked,
    onSetDri,
    onRemove,
}: AssigneeRowActionsProps) {
    return (
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
            <button
                type="button"
                disabled={rosterLocked}
                title={
                    rosterLocked
                        ? "Only the DRI, the creator or an admin can change the DRI."
                        : isDri
                        ? `${name} is the DRI (directly responsible individual). Click another assignee's "Set DRI" to change.`
                        : `Set ${name} as DRI -- the person responsible for unblocking the team.`
                }
                className={cn(
                    "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border px-2.5 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                    rosterLocked ? "cursor-default opacity-60" : "cursor-pointer",
                    isDri
                        ? "border-primary/50 bg-primary/15 text-primary hover:bg-primary/25"
                        : "border-border bg-background text-muted-foreground hover:border-primary/45 hover:bg-muted/60 hover:text-primary"
                )}
                onClick={rosterLocked ? undefined : () => onSetDri(userPid)}
                aria-pressed={isDri}
                aria-label={isDri ? `${name} is current DRI` : `Set ${name} as DRI`}
            >
                <Crown className="h-4 w-4 shrink-0" aria-hidden />
                <span className="whitespace-nowrap">{isDri ? "DRI" : "Set DRI"}</span>
            </button>
            <button
                type="button"
                disabled={rosterLocked}
                title={rosterLocked ? "Only the DRI, the creator or an admin can remove assignees." : undefined}
                className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border bg-background text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                    rosterLocked ? "cursor-default opacity-40" : "cursor-pointer hover:border-destructive/40 hover:text-destructive"
                )}
                onClick={rosterLocked ? undefined : () => onRemove(userPid)}
                aria-label={`Remove ${name}`}
            >
                <X className="h-4 w-4" aria-hidden />
            </button>
        </div>
    );
}
